/**
 * Product Media Slideshow
 * Handles the mobile slideshow for product media gallery
 */
class ProductMediaSlideshow {
  constructor() {
    console.log("Initializing ProductMediaSlideshow");
    this.initSlideshows();
    this.setupEventListeners();
    this.isRefreshing = false; // Flag to prevent multiple concurrent refreshes
    
    // Touch event variables
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchCurrentX = 0;
    this.swipeThreshold = 50; // Minimum swipe distance in pixels
    this.currentSwipeSlideshow = null; // Track which slideshow is being swiped
    this.isAnimating = false; // Flag to prevent multiple animations
    
    // Add CSS for transitions
    this.addTransitionStyles();
    
    // Initialize swipe functionality
    this.setupSwipeListeners();
  }

  initSlideshows() {
    const slideshows = document.querySelectorAll('.mobile-slideshow');
    
    slideshows.forEach(slideshow => {
      if (slideshow.dataset.initialized === 'true') return;
      
      const slides = slideshow.querySelectorAll('.mobile-slideshow__slide');
      if (!slides.length) return;
      
      // Set the first visible slide as active and assign visible indices
      const visibleSlides = Array.from(slides).filter(slide => !slide.classList.contains('hidden'));
      
      // Assign visible indices
      visibleSlides.forEach((slide, index) => {
        slide.setAttribute('data-visible-index', index + 1);
      });
      
      if (visibleSlides.length > 0) {
        visibleSlides[0].classList.add('is-active');
      } else if (slides[0]) {
        slides[0].classList.add('is-active');
        slides[0].setAttribute('data-visible-index', 1);
      }
      
      // Find navigation elements
      const galleryContainer = slideshow.closest('.product__media-gallery');
      if (!galleryContainer) return;
      
      // Generate dots based on visible slides
      this.generateDots(slideshow, galleryContainer);
      
      // Mark as initialized
      slideshow.dataset.initialized = 'true';
      
      // Important: DON'T add any click event listeners to navigation buttons here
      // Let variant-image-filter.js handle all navigation to prevent double-advancing
    });
  }

  setupEventListeners() {

    const productForm = document.querySelector('product-form');

    // Watch for changes to variants that may affect image visibility
    productForm.addEventListener('change', function() {        
        // Let variant-image-filter.js handle this, we'll respond to gallery:filtered
        // We'll ensure the slideshow state is in sync
          const slideshow = document.querySelector('.mobile-slideshow');
          if (slideshow) {
            
            // Ensure visible-index values are set correctly
            const visibleSlides = Array.from(slideshow.querySelectorAll('.mobile-slideshow__slide:not(.hidden)'));
            
            
            // Always reassign visible indices after variant changes
            visibleSlides.forEach((slide, index) => {
              slide.setAttribute('data-visible-index', (index + 1).toString());
            });
            
            if (visibleSlides.length) {
              visibleSlides.forEach(slide => slide.classList.remove('is-active'));
              visibleSlides[0].classList.add('is-active');
              
              // Force immediate dot update via variant-image-filter
              if (window.variantImageFilter) {
                window.variantImageFilter.updateDots(visibleSlides);
                window.variantImageFilter.updateActiveDot(0);
              }
            }
          }
    });
    
    // Refresh on resize (mobile/desktop switch)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.refreshSlideshows();
      }, 250);
    });
  }
  
  addTransitionStyles() {
    // Add CSS for slide transitions if not already present
    if (!document.getElementById('slideshow-transition-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'slideshow-transition-styles';
      styleEl.textContent = `
        .mobile-slideshow {
          position: relative;
          overflow: hidden;
        }
        .mobile-slideshow__slide {
          transition: transform 0.3s ease;
          will-change: transform;
        }
        .mobile-slideshow.swiping .mobile-slideshow__slide {
          transition: none; /* No transition during active swipe */
        }
        .mobile-slideshow__slide.swipe-next,
        .mobile-slideshow__slide.swipe-prev {
          position: absolute;
          top: 0;
          height: 100%;
          width: 100%;
          z-index: 1;
          opacity: 1 !important;
        }
        .mobile-slideshow__slide.is-active {
          z-index: 2;
        }
      `;
      document.head.appendChild(styleEl);
    }
  }
  
  setupSwipeListeners() {
    const slideshows = document.querySelectorAll('.mobile-slideshow');
    
    slideshows.forEach(slideshow => {
      slideshow.addEventListener('touchstart', (e) => this.handleTouchStart(e, slideshow), { passive: true });
      slideshow.addEventListener('touchmove', (e) => this.handleTouchMove(e, slideshow), { passive: true });
      slideshow.addEventListener('touchend', (e) => this.handleTouchEnd(e, slideshow), { passive: true });
      slideshow.addEventListener('touchcancel', (e) => this.handleTouchCancel(e, slideshow), { passive: true });
    });
  }
  
  handleTouchStart(event, slideshow) {
    if (this.isAnimating) return;
    
    // Check if there's only one visible slide - if so, disable swipe
    const visibleSlides = Array.from(slideshow.querySelectorAll('.mobile-slideshow__slide:not(.hidden)'));
    if (visibleSlides.length <= 1) {
      return; // Don't allow swipe for single slide
    }
    
    this.touchStartX = event.changedTouches[0].screenX;
    this.touchCurrentX = this.touchStartX;
    this.currentSwipeSlideshow = slideshow;
    // Add swiping class to disable transitions during swipe
    slideshow.classList.add('swiping');
    // For 2 slides, we need to know the intended swipe direction
    this.swipeDirection = null; // Reset
  }

  handleTouchMove(event, slideshow) {
    if (this.currentSwipeSlideshow !== slideshow) return;
    this.touchCurrentX = event.changedTouches[0].screenX;
    const swipeDistance = this.touchCurrentX - this.touchStartX;
    // Set swipe direction for 2-slide case
    if (Math.abs(swipeDistance) > 0) {
      this.swipeDirection = swipeDistance > 0 ? 'prev' : 'next';
    }
    // Prepare adjacent slides for swiping (for 2 slides, needs direction)
    this.prepareAdjacentSlides(slideshow, this.swipeDirection);
    // Update slide positions based on swipe distance
    this.updateSlidesPosition(slideshow, swipeDistance);
  }
  
  handleTouchEnd(event, slideshow) {
    if (this.currentSwipeSlideshow !== slideshow) return;
    
    this.touchEndX = event.changedTouches[0].screenX;
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    // Remove swiping class to re-enable transitions
    slideshow.classList.remove('swiping');
    
    if (Math.abs(swipeDistance) > this.swipeThreshold) {
      // Complete the swipe animation
      const direction = swipeDistance > 0 ? 'prev' : 'next';
      this.completeSwipeAnimation(slideshow, direction);
    } else {
      // Reset positions if swipe wasn't enough
      this.resetSlidesPosition(slideshow);
    }
    
    this.currentSwipeSlideshow = null;
  }
  
  handleTouchCancel(event, slideshow) {
    if (this.currentSwipeSlideshow !== slideshow) return;
    
    // Reset on cancel
    slideshow.classList.remove('swiping');
    this.resetSlidesPosition(slideshow);
    this.currentSwipeSlideshow = null;
  }
  
  prepareAdjacentSlides(slideshow, swipeDirection) {
    // Get all visible slides
    const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
    const visibleElements = Array.from(allSlides).filter(slide => !slide.classList.contains('hidden'));

    // Sort by visible index
    const visibleSlides = [...visibleElements].sort((a, b) => {
      return parseInt(a.getAttribute('data-visible-index') || 0) - 
             parseInt(b.getAttribute('data-visible-index') || 0);
    });

    if (visibleSlides.length <= 1) return;

    // Find current active slide
    const currentSlide = slideshow.querySelector('.is-active');
    if (!currentSlide) return;

    const currentIndex = visibleSlides.findIndex(slide => slide === currentSlide);
    if (currentIndex === -1) return;

    // Calculate next and previous indices with wrap-around
    const nextIndex = (currentIndex + 1) % visibleSlides.length;
    const prevIndex = (currentIndex - 1 + visibleSlides.length) % visibleSlides.length;

    // Remove any previous swipe classes
    visibleSlides.forEach(slide => {
      slide.classList.remove('swipe-next', 'swipe-prev');
      slide.style.transform = '';
    });

    // Special handling for 2 slides: only assign one class depending on swipe direction
    if (visibleSlides.length === 2 && swipeDirection) {
      if (swipeDirection === 'next') {
        visibleSlides[nextIndex].classList.add('swipe-next');
        visibleSlides[nextIndex].style.transform = 'translateX(100%)';
      } else if (swipeDirection === 'prev') {
        visibleSlides[prevIndex].classList.add('swipe-prev');
        visibleSlides[prevIndex].style.transform = 'translateX(-100%)';
      }
    } else {
      // Mark adjacent slides
      visibleSlides[nextIndex].classList.add('swipe-next');
      visibleSlides[nextIndex].style.transform = 'translateX(100%)';
      visibleSlides[prevIndex].classList.add('swipe-prev');
      visibleSlides[prevIndex].style.transform = 'translateX(-100%)';
    }
  }
  
  updateSlidesPosition(slideshow, swipeDistance) {
    const currentSlide = slideshow.querySelector('.is-active');
    const nextSlide = slideshow.querySelector('.swipe-next');
    const prevSlide = slideshow.querySelector('.swipe-prev');
    
    const slideWidth = currentSlide ? currentSlide.offsetWidth : 0;
    if (slideWidth === 0) return;
    
    // Calculate percentage moved
    const percentMoved = (swipeDistance / slideWidth) * 100;
    
    // Limit movement to range [-100%, 100%]
    const limitedPercent = Math.max(-100, Math.min(100, percentMoved));
    
    // Move current slide
    if (currentSlide) {
      currentSlide.style.transform = `translateX(${limitedPercent}%)`;
    }
    
    // Move next slide (coming from right)
    if (nextSlide) {
      nextSlide.style.transform = `translateX(${100 + limitedPercent}%)`;
    }
    
    // Move prev slide (coming from left)
    if (prevSlide) {
      prevSlide.style.transform = `translateX(${-100 + limitedPercent}%)`;
    }
  }
  
  resetSlidesPosition(slideshow) {
    const currentSlide = slideshow.querySelector('.is-active');
    const nextSlide = slideshow.querySelector('.swipe-next');
    const prevSlide = slideshow.querySelector('.swipe-prev');
    
    // Animate slides back to their original positions
    if (currentSlide) currentSlide.style.transform = '';
    if (nextSlide) nextSlide.style.transform = 'translateX(100%)';
    if (prevSlide) prevSlide.style.transform = 'translateX(-100%)';
    
    // Wait for animation to complete
    setTimeout(() => {
      // Clean up classes after transition
      const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
      allSlides.forEach(slide => {
        if (!slide.classList.contains('is-active')) {
          slide.classList.remove('swipe-next', 'swipe-prev');
          slide.style.transform = '';
        }
      });
    }, 300); // Match the transition duration
  }
  
  completeSwipeAnimation(slideshow, direction) {
    this.isAnimating = true;
    
    const currentSlide = slideshow.querySelector('.is-active');
    const targetSlide = direction === 'next' ? 
                        slideshow.querySelector('.swipe-next') : 
                        slideshow.querySelector('.swipe-prev');
    
    if (!currentSlide || !targetSlide) {
      this.isAnimating = false;
      return;
    }
    
    // Finish the animation
    if (direction === 'next') {
      currentSlide.style.transform = 'translateX(-100%)';
      targetSlide.style.transform = 'translateX(0)';
    } else {
      currentSlide.style.transform = 'translateX(100%)';
      targetSlide.style.transform = 'translateX(0)';
    }
    
    // Wait for animation to complete
    setTimeout(() => {
      // Update active state
      currentSlide.classList.remove('is-active');
      targetSlide.classList.add('is-active');
      targetSlide.classList.remove('swipe-next', 'swipe-prev');
      
      // Clean up all slides
      const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
      allSlides.forEach(slide => {
        slide.classList.remove('swipe-next', 'swipe-prev');
        slide.style.transform = '';
      });
      
      // Update dots
      const galleryContainer = slideshow.closest('.product__media-gallery');
      if (galleryContainer) {
        const visibleSlides = Array.from(slideshow.querySelectorAll('.mobile-slideshow__slide:not(.hidden)'))
          .sort((a, b) => {
            return parseInt(a.getAttribute('data-visible-index') || 0) - 
                   parseInt(b.getAttribute('data-visible-index') || 0);
          });
        
        const newActiveIndex = visibleSlides.findIndex(slide => slide === targetSlide);
        if (newActiveIndex !== -1) {
          this.updateDots(galleryContainer, newActiveIndex);
        }
      }
      
      this.isAnimating = false;
    }, 300); // Match the transition duration
  }

  refreshSlideshows() {
    
    // Prevent multiple concurrent refreshes
    if (this.isRefreshing) {
      return;
    }
    
    this.isRefreshing = true;
    
    const slideshows = document.querySelectorAll('.mobile-slideshow');
    
    slideshows.forEach((slideshow, slideIndex) => {
      
      // Get all slides and filter to just visible ones
      const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
      
      const visibleSlides = Array.from(allSlides).filter(slide => !slide.classList.contains('hidden'));
      
      
      // Use the original DOM ordering of the elements
      const orderedSlides = Array.from(allSlides).filter(slide => visibleSlides.includes(slide));
      
      // If there are no visible slides, show the first one
      if (!visibleSlides.length && allSlides.length) {
        allSlides[0].classList.remove('hidden');
        orderedSlides.push(allSlides[0]);
      }
      
      // Assign sequential visible indices to visible slides in DOM order
      orderedSlides.forEach((slide, index) => {
        const oldIndex = slide.getAttribute('data-visible-index');
        const newIndex = index + 1;
        slide.setAttribute('data-visible-index', newIndex);
      });
      
      // If no slide is active, activate the first visible one
      const activeSlide = slideshow.querySelector('.is-active');
      if (!activeSlide || activeSlide.classList.contains('hidden')) {
        // Remove active class from all slides first
        allSlides.forEach(slide => {
          if (slide.classList.contains('is-active')) {
          }
          slide.classList.remove('is-active');
        });
        
        // Set the first visible slide as active
        if (orderedSlides.length) {
          orderedSlides[0].classList.add('is-active');
          
          // Also update the dots to match the first slide
          if (window.variantImageFilter) {
            window.variantImageFilter.updateActiveDot(0);
          }
        }
      } else {
      }
      
    });
    
    this.isRefreshing = false;
  }

  navigateSlide(slideshow, direction) {
    // Don't navigate if a refresh is in progress
    if (this.isRefreshing) return;
    
    // Get all slides that are visible and in DOM order
    const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
    const visibleElements = Array.from(allSlides).filter(slide => !slide.classList.contains('hidden'));
    
    // Sort visible slides by their visual order (data-visible-index)
    const visibleSlides = [...visibleElements].sort((a, b) => {
      const aIndex = parseInt(a.getAttribute('data-visible-index') || 0);
      const bIndex = parseInt(b.getAttribute('data-visible-index') || 0);
      return aIndex - bIndex;
    });
    
    if (!visibleSlides.length) return;
    
    const currentSlide = slideshow.querySelector('.is-active');
    if (!currentSlide) return;
    
    // Find current index based on position in the visibleSlides array
    const currentIndex = visibleSlides.findIndex(slide => slide === currentSlide);
    if (currentIndex === -1) return;
    
    // Calculate next/previous index with wrap-around
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % visibleSlides.length;
    } else {
      nextIndex = (currentIndex - 1 + visibleSlides.length) % visibleSlides.length;
    }
    
    // Change active slide
    visibleSlides.forEach(slide => slide.classList.remove('is-active'));
    visibleSlides[nextIndex].classList.add('is-active');
    
    // Update dots
    const galleryContainer = slideshow.closest('.product__media-gallery');
    if (galleryContainer) {
      this.updateDots(galleryContainer, nextIndex);
    }
  }

  generateDots(slideshow, galleryContainer) {
    // NOTE: This method is now primarily used during initialization
    // For dynamic updates after filtering, we use variant-image-filter.js's updateDots method
    
    // Get all visible slides in order of their visible-index
    const allSlides = slideshow.querySelectorAll('.mobile-slideshow__slide');
    const visibleSlides = Array.from(allSlides)
      .filter(slide => !slide.classList.contains('hidden'))
      .sort((a, b) => {
        const aIndex = parseInt(a.getAttribute('data-visible-index') || 0);
        const bIndex = parseInt(b.getAttribute('data-visible-index') || 0);
        return aIndex - bIndex;
      });
    
    // Find dots container
    const dotsContainer = galleryContainer.querySelector('.slideshow-dots');
    const paginationContainer = galleryContainer.querySelector('.mobile-slideshow-pagination');
    const controlsContainer = galleryContainer.querySelector('.mobile-slideshow-controls');
    
    if (!dotsContainer) return;
    
    // Clear existing dots
    dotsContainer.innerHTML = '';
    
    // If only one slide, don't show dots or controls
    if (visibleSlides.length <= 1) {
      if (paginationContainer) {
        paginationContainer.style.display = 'none';
      }
      if (controlsContainer) {
        controlsContainer.style.display = 'none';
      }
      return;
    }
    
    // Show controls and pagination when more than 1 slide
    if (paginationContainer) {
      paginationContainer.style.display = '';
    }
    if (controlsContainer) {
      controlsContainer.style.display = '';
    }
    
    // Find the active slide index
    const activeSlide = slideshow.querySelector('.is-active');
    let activeIndex = -1;
    
    if (activeSlide) {
      activeIndex = visibleSlides.findIndex(slide => slide === activeSlide);
    }
    
    if (activeIndex === -1) activeIndex = 0;
    
    // Create dots for visible slides but use variant-image-filter's goToSlide
    visibleSlides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.classList.add('slideshow-dot');
      if (index === activeIndex) {
        dot.classList.add('active');
      }
      dot.setAttribute('data-slide-index', index + 1);
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      
      // Add click event that uses variant-image-filter instead
      dot.addEventListener('click', () => {
        if (window.variantImageFilter) {
          window.variantImageFilter.goToSlide(slideshow, index + 1);
        }
      });
      
      dotsContainer.appendChild(dot);
    });
  }

  updateDots(galleryContainer, activeIndex) {
    const dots = galleryContainer.querySelectorAll('.slideshow-dot');
    
    dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  handleSwipe(slideshow) {
    // This method is now replaced by the more visual completeSwipeAnimation
    // keeping for backward compatibility
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) > this.swipeThreshold) {
      const direction = swipeDistance > 0 ? 'prev' : 'next';
      this.navigateSlide(slideshow, direction);
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const productForm = document.querySelector('product-form');

  window.productMediaSlideshow = new ProductMediaSlideshow();
  
  // Add a direct event listener to monitor variant changes
  productForm.addEventListener('change', function(event) {
  });
});
